import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment as ENV } from '../../environments/environment';
import {
    IPlanSummaryAlert,
    IPlanSummaryDocument,
    IPlanSummaryDocumentFile,
    IPlanSummaryEventSession, IPlanSummaryLecturer, IPlanSummaryManipulation, IPlanSummaryMidtermExam, IPlanSummaryProgress, IPlanSummaryResident,
    IPlanSummaryRotations, IPlanSummarySeminar, IPlanSummaryShifts, IPlanSummarySupervisor, IPlanSummaryUpcomingCourse
} from '../models/PlanSummary';

/**
 * Study plan summary service
 */
@Injectable({ providedIn: "root" })
export class PlanSummaryService {
    constructor(public http: HttpClient) { }

    public get apiUrl(): string { return `${ENV.apiUrl}/planSummary` }

    /**
     * Get rotations.
     * @param options
     */
    getRotations(options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<IPlanSummaryRotations> {
        const url = `${this.apiUrl}/rotations`;
        const params = this.buildQueryParams(options);

        return this.http.get<IPlanSummaryRotations>(url, { params });
    }

    /**
     * Get manipulations.
     * @param options
     */
    getManipulations(options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<IPlanSummaryManipulation[]> {
        const url = `${this.apiUrl}/manipulations`;
        const params = this.buildQueryParams(options);

        return this.http.get<IPlanSummaryManipulation[]>(url, { params });
    }

    /**
     * Get seminars.
     * @param options
     */
    getSeminars(options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<IPlanSummarySeminar[]> {
        const url = `${this.apiUrl}/seminars`;
        const params = this.buildQueryParams(options);

        return this.http.get<IPlanSummarySeminar[]>(url, { params });
    }

    /**
     * Get on-call shifts.
     * @param options
     */
    getShifts(options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<IPlanSummaryShifts> {
        const url = `${this.apiUrl}/shifts`;
        const params = this.buildQueryParams(options);

        return this.http.get<IPlanSummaryShifts>(url, { params });
    }

    /**
     * Get supervisor.
     * @param options
     */
    getSupervisor(options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<IPlanSummarySupervisor> {
        const url = `${this.apiUrl}/supervisor`;
        const params = this.buildQueryParams(options);

        return this.http.get<IPlanSummarySupervisor>(url, { params });
    }

    /**
     * Get resident by ID or email.
     * @param options
     */
    getResident(options: { studentId?: string, studentEmail?: string }): Observable<IPlanSummaryResident[]> {
        const url = `${this.apiUrl}/resident`;
        const params = this.buildQueryParams(options);

        return this.http.get<IPlanSummaryResident[]>(url, { params });
    }

    /**
     * Get plan summary report download URL.
     * @param fileId
     * @param options
     */
    getFileUrl(fileId: string, options?: { studentId?: string, studentEmail?: string }): string {
        let url = `${this.apiUrl}/files/${fileId}`;

        if (options) {
            url += (options.studentId ? `?studentId=${options.studentId}` : options.studentEmail ? `?email=${options.studentEmail}` : '');
        }

        return url;
    }

    /**
     * Initialize an event session.
     * @param options
     */
    initEventSession(options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<IPlanSummaryEventSession> {
        const url = `${this.apiUrl}/eventSession`;
        const params = this.buildQueryParams(options);

        return this.http.post<IPlanSummaryEventSession>(url, null, { params });
    }

    /**
     * Get lecturers.
     * @param options
     */
    getLecturers(search: string, options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<IPlanSummaryLecturer[]> {
        const url = `${this.apiUrl}/lecturers`;
        const params = this.buildQueryParams({ search, ...options });

        return this.http.get<IPlanSummaryLecturer[]>(url, { params });
    }

    /**
     * Add a lecturer.
     * @param lecturerId
     * @param rotationId
     * @param options
     */
    addLecturer(lecturerId: string, lecturerName: string, lecturerSurname:string , rotationId, options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<any> {
        const url = `${this.apiUrl}/lecturers`;
        const params = this.buildQueryParams(options);

        return this.http.post<any>(url, { lecturerId, lecturerName, lecturerSurname, rotationId }, { params });
    }

    /**
     * Remove a lecturer.
     * @param lecturerId
     * @param rotationId
     * @param options
     */
    removeLecturer(lecturerId: string, lecturerName: string, lecturerSurname:string, rotationId, options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<any> {
        const url = `${this.apiUrl}/lecturers`;
        const params = this.buildQueryParams(options);

        return this.http.delete<any>(url, { params, body: { lecturerId, lecturerName, lecturerSurname, rotationId } });
    }

    /**
     * Get student documents.
     * @param studentUpn 
     */
    getDocuments(studentUpn?: string) {
        const url = `${this.apiUrl}/documents`;
        const params = studentUpn ? { studentUpn } : {};
        
        return this.http.get<IPlanSummaryDocument[]>(url, { params });
    }

    /**
     * Get document file list.
     * @param documentId 
     * @param studentUpn 
     */
    getDocumentFiles(documentId: number, studentUpn?: string) {
        const url = `${this.apiUrl}/documents/${documentId}/files`;
        const params = studentUpn ? { studentUpn } : {};
        
        return this.http.get<IPlanSummaryDocumentFile[]>(url, { params });
    }

    /**
     * Get document file download URL.
     * @param documentId 
     * @param fileId 
     * @param fileName 
     * @param studentUpn 
     */
    getFileDownloadUrl(documentId: number, fileId: number, fileName: string, studentUpn?: string) {
        let params = `name=${encodeURIComponent(fileName)}`;

        if (studentUpn) params += `&studentUpn=${studentUpn}`;

        return `${this.apiUrl}/documents/${documentId}/files/${fileId}/download?${params}`;
    }

    /**
     * Get resident totals.
     * @param options
     */
    getTotals(options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<IPlanSummaryProgress> {
        const url = `${this.apiUrl}/totals`;
        const params = this.buildQueryParams(options);

        return this.http.get<IPlanSummaryProgress>(url, { params });
    }

    /**
     * Get alerts.
     * @param studentUpn
     */
    getAlerts(studentUpn?: string): Observable<IPlanSummaryAlert[]> {
        const url = `${this.apiUrl}/alerts`;
        const params = this.buildQueryParams({ studentUpn });

        return this.http.get<IPlanSummaryAlert[]>(url, { params });
    }

    /**
     * Update medical person data.
     * @param data
     * @param options
     */
    updateMedicalPerson(
        data: { medicalPersonId?: string, studentId: string },
        options: { programId?: string, studentId?: string, studentEmail?: string }
    ): Observable<any> {
        const url = `${this.apiUrl}/medicalPerson`;
        const params = this.buildQueryParams(options);

        return this.http.post<any>(url, data, { params });
    }

    /**
     * Get student upcoming courses by year.
     * @param options
     */
    getUpcomingCourses(options: { programId?: string, studentId?: string, studentEmail?: string }): Observable<IPlanSummaryUpcomingCourse[]> {
        const url = `${this.apiUrl}/upcomingCourses`;
        const params = this.buildQueryParams(options);

        return this.http.get<IPlanSummaryUpcomingCourse[]>(url, { params });
    }

    /**
     * Get mid-term examinations.
     * @param options
     */
    getMidtermExams(studentId: string): Observable<IPlanSummaryMidtermExam[]> {
        const url = `${this.apiUrl}/midtermExams`;
        const params = this.buildQueryParams({ studentId });

        return this.http.get<IPlanSummaryMidtermExam[]>(url, { params });
    }

    private buildQueryParams(options: any): any {
        const params: any = {};

        for (const k in options) {
            const val = options[k];

            if (val !== undefined && val !== null) {
                if (typeof val == 'string') {
                    if (val) params[k] = val;
                } else {
                    params[k] = val;
                }
            }
        }

        return params;
    }
}
